import classNames from "classnames"
import type Swiper from "swiper"
import { FreeMode, Thumbs } from "swiper/modules"
import { Swiper as SwiperComponent, SwiperSlide } from "swiper/react"

import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import CloseIcon from "@components/elements/V2/Icons/CloseIcon"
import Typography from "@components/elements/V2/Typography"
import Video from "@components/elements/V2/Video"
import GalleryNavButton from "@components/V2/Sections/Info/GalletNavButton"
import { Color } from "@constants/V2/color"
import { useBodyScrollLock } from "@hooks/V2/useBodyScrollLock"
import { useIsBreakpoint } from "@utils/V2/screen"
import React, { useEffect, useRef, useState } from "react"

type GalleryItems = {
  imageFilename: string
  videoFilename?: string
  imageAlt: string
}

type Props = {
  open: boolean
  handleClose: (value: boolean) => void
  closeIconColor?: Color
  galleryItems: GalleryItems[]
  initialSlide?: number
  productName?: string
}

export default function GalleryModal({
  open,
  handleClose,
  galleryItems,
  closeIconColor = Color.Black,
  initialSlide = 0,
  productName,
}: Props) {
  const [thumbsSwiper, setThumbsSwiper] = useState<Swiper>()
  const [navigationState, setNavigationState] = useState({
    isBeginning: true,
    isEnd: false,
  })
  const [activeThumbIndex, setActiveThumbIndex] = useState(0)
  const [isZoomed, setIsZoomed] = useState(false)
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 })

  const isMobile = useIsBreakpoint("mobile")
  const isTablet = useIsBreakpoint("tablet")

  const thumbDirection = isMobile || isTablet ? "horizontal" : "vertical"
  const activeThumbBorderColor = "border-blue"

  const swiperReference = useRef<Swiper | null>(null)

  const handleSlide = (step: "prev" | "next") => {
    if (!swiperReference.current) return
    step === "prev"
      ? swiperReference.current.slidePrev()
      : swiperReference.current.slideNext()
  }

  const handleSlideChange = (swiper: Swiper) => {
    setNavigationState({
      isBeginning: swiper.isBeginning,
      isEnd: swiper.isEnd,
    })
    setActiveThumbIndex(swiper.realIndex)
    setIsZoomed(false)
    setZoomOrigin({ x: 50, y: 50 })
  }

  const getImageOrigin = (e: React.MouseEvent<HTMLDivElement>) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect()

    return {
      x: ((e.clientX - left) / width) * 100,
      y: ((e.clientY - top) / height) * 100,
    }
  }

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isZoomed) setZoomOrigin(getImageOrigin(e))
    setIsZoomed((prev) => !prev)
  }

  const handleImageMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isZoomed) setZoomOrigin(getImageOrigin(e))
  }

  useBodyScrollLock(open)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose(false)
    }

    if (open) document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [open, handleClose])

  return (
    <div
      data-testid="global-modal"
      className={classNames(
        "fixed left-0 top-0 z-50 flex h-screen w-full items-center justify-center overflow-y-auto bg-charcoal/90 transition-all duration-300 sm:p-4 md:px-40",
        open ? "opacity-100" : "pointer-events-none opacity-0"
      )}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Product gallery modal"
        className="relative z-10 flex h-screen w-full flex-col overflow-hidden rounded-none bg-white sm:h-[50vh] sm:rounded-20 md:h-[60vh] lg:h-[80vh]"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-charcoal/10 px-4 py-3 sm:px-24 sm:py-4 md:px-8 md:py-5">
          {productName && (
            <Typography
              text={productName}
              size="body-lg"
              font="grotesk"
              weight="medium"
              color={Color.Charcoal}
              className="mr-4 truncate"
            />
          )}
          <button
            data-testid="modal-close-button"
            aria-label="Close gallery modal"
            className="ml-auto size-[28px] shrink-0 cursor-pointer transition-all duration-300 hover:opacity-70"
            onClick={() => handleClose(false)}
          >
            <CloseIcon
              hideBorder
              width="24px"
              height="24px"
              strokeWidth="2"
              strokeColor={closeIconColor}
            />
          </button>
        </div>

        <div className="flex flex-1 flex-col-reverse gap-3 overflow-hidden p-4 sm:p-5 md:flex-row md:gap-0 md:p-8">
          <div className="shrink-0 pb-[10px] md:ml-[47px] md:h-full md:w-[116px] md:overflow-hidden md:pb-0 md:pr-4 md:pt-[50px]">
            {galleryItems.length > 1 && (
              <>
                <SwiperComponent
                  modules={[FreeMode]}
                  key={thumbDirection}
                  onSwiper={(swiper) => setThumbsSwiper(swiper)}
                  direction={thumbDirection}
                  spaceBetween={8}
                  slidesPerView="auto"
                  watchSlidesProgress={true}
                  freeMode={true}
                  observer={true}
                  observeParents={true}
                  className="thumbs-swiper h-full [&>.swiper-wrapper]:flex [&>.swiper-wrapper]:flex-row md:[&>.swiper-wrapper]:flex-col"
                >
                  {galleryItems.map((galleryItem, index) => (
                    <SwiperSlide
                      key={`thumb-${galleryItem.imageFilename}-${index}`}
                      className={classNames(
                        "!h-[75px] !w-[100px] cursor-pointer overflow-hidden rounded-8 border transition-colors md:border-[2px]",
                        index === activeThumbIndex
                          ? activeThumbBorderColor
                          : "border-charcoal/30"
                      )}
                    >
                      {galleryItem.videoFilename ? (
                        <Video
                          video={galleryItem.videoFilename}
                          coverImage={galleryItem.imageFilename}
                          ImageAlt="image"
                          aspectRatio="1:1"
                          playbackMode="autoplay"
                          playOnMobile={false}
                          rounded
                        />
                      ) : (
                        <GatsbyStoryblokImage
                          testId="gallery-slide-thumbnail-image"
                          image={galleryItem.imageFilename}
                          alt={galleryItem.imageAlt || "Product thumbnail"}
                          aspectRatio="4:3"
                          imageObjectFit="cover"
                          className="h-full w-full"
                        />
                      )}
                    </SwiperSlide>
                  ))}
                </SwiperComponent>

                <div className="pointer-events-none absolute bottom-0 left-0 z-10 h-12 w-full bg-gradient-to-t from-white to-transparent"></div>
              </>
            )}
          </div>

          <div className="group relative flex w-full flex-1 items-center justify-center overflow-hidden md:p-24">
            <SwiperComponent
              modules={[Thumbs]}
              slidesPerView={1}
              grabCursor={true}
              initialSlide={initialSlide}
              loop={galleryItems.length > 1}
              enabled={galleryItems.length > 1}
              thumbs={{
                swiper:
                  thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
              }}
              onSwiper={(swiper: Swiper) => {
                swiperReference.current = swiper
              }}
              onSlideChange={handleSlideChange}
            >
              {galleryItems.map((galleryItem, index) => (
                <SwiperSlide
                  key={`${galleryItem.imageFilename}-${index}`}
                  className="flex items-center justify-center"
                >
                  <div className="relative max-h-[490px] w-full max-w-[660px] overflow-hidden rounded-16 bg-light-gray md:max-h-[840px] md:max-w-[1120px]">
                    {galleryItem.videoFilename ? (
                      <Video
                        video={galleryItem.videoFilename}
                        coverImage={galleryItem.imageFilename}
                        ImageAlt="image"
                        aspectRatio="4:3"
                        playbackMode="autoplay"
                        playOnMobile={false}
                        rounded
                      />
                    ) : (
                      <div
                        className={classNames(
                          "transition-transform duration-300",
                          {
                            "scale-[175%] cursor-zoom-out": isZoomed,
                            "cursor-zoom-in": !isZoomed,
                          }
                        )}
                        style={{
                          transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
                        }}
                        onClick={handleImageClick}
                        onMouseMove={handleImageMouseMove}
                      >
                        <GatsbyStoryblokImage
                          testId="gallery-slide-image"
                          image={galleryItem.imageFilename}
                          alt={galleryItem.imageAlt || "Product thumbnail"}
                          aspectRatio="1:1"
                          quality={80}
                          rounded
                        />
                      </div>
                    )}
                  </div>
                </SwiperSlide>
              ))}
            </SwiperComponent>

            <GalleryNavButton
              onClick={() => handleSlide("prev")}
              direction="left"
              className={classNames(
                "absolute left-2 top-1/2 z-10 hidden -translate-y-1/2 !bg-light-gray sm:flex md:left-5 md:hidden md:group-hover:flex",
                { "!opacity-30": navigationState.isBeginning }
              )}
            />

            <GalleryNavButton
              onClick={() => handleSlide("next")}
              direction="right"
              className={classNames(
                "absolute right-2 top-1/2 z-10 hidden -translate-y-1/2 !bg-light-gray sm:flex md:right-5 md:hidden md:group-hover:flex",
                { "!opacity-30": navigationState.isEnd }
              )}
            />
          </div>
        </div>
      </div>

      <div
        className="fixed inset-0 -z-10 bg-transparent"
        onClick={() => handleClose(false)}
      />
    </div>
  )
}
